import { useState } from 'react';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
  Link
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box } from '@mui/system';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import useUsers from 'src/services/useUsers';
import { USERS_FORGOT_PASSWORD } from 'src/route';
import { getLogoByHostname } from 'src/utils/getLogoByHostname';
import { usePostHog } from 'posthog-js/react';

export interface ISignIn {
  email: string;
  password: string;
  companyName: string;
}

function validationSchema() {
  return Yup.object({
    email: Yup.string().email().required(),
    password: Yup.string().required(),
    companyName: Yup.string()
  });
}

const FormSignIn = () => {
  const posthog = usePostHog();
  const { postUserSignin, responseUserSignIn } = useUsers();
  const { handleChange, handleSubmit, errors, values, touched } =
    useFormik<ISignIn>({
      initialValues: {
        email: '',
        password: '',
        companyName: 'demo'
      },
      validationSchema: validationSchema(),
      onSubmit: (value) => {
        posthog?.capture('Sign in', { Email: value.email });
        postUserSignin({
          variables: value
        });
      }
    });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const handleClickShowPassword = (index) => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const { logo, width } = getLogoByHostname(true);

  return (
    <Box
      sx={{
        width: '536px'
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item lg={12}>
            <img src={logo} alt={'Logo'} style={{ width: width }} />
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12}>
            <Typography variant="h1">Login</Typography>
          </Grid>

          <Grid item lg={12}>
            <FormControl fullWidth size="medium">
              <FormLabel>Email Address</FormLabel>
              <OutlinedInput
                name="email"
                onChange={handleChange}
                error={errors.email && touched.email}
                value={values.email}
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.email && touched.email && errors.email}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item lg={12}>
            <FormControl fullWidth size="medium">
              <FormLabel>Password</FormLabel>
              <OutlinedInput
                name="password"
                onChange={handleChange}
                error={errors.password && touched.password}
                value={values.password}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword(0)}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword[0] ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.password && touched.password && errors.password}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid
            item
            lg={12}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Link href={USERS_FORGOT_PASSWORD} underline="none">
              Forgot your password?
            </Link>
          </Grid>
          <Grid item lg={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              sx={{
                mt: 2,
                px: 8
              }}
              disabled={responseUserSignIn.loading}
            >
              Sign In
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default FormSignIn;
